import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "agent" });
  const [showForm, setShowForm] = useState(false);
  const { user } = useAuth();

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { if (user?.role === "admin") load(); }, [user]); // eslint-disable-line

  if (user && user.role !== "admin") {
    return <div className="rounded-2xl border border-border bg-card p-10 text-center"><p className="font-semibold">Admins only</p><p className="mt-1 text-sm text-muted-foreground">User management requires the admin role.</p></div>;
  }

  const create = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success("User created — a secure set-password link was emailed to them.", { duration: 8000 });
      setForm({ name: "", email: "", role: "agent" }); setShowForm(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const patch = async (id, body) => {
    try { await api.patch(`/admin/users/${id}`, body); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  const reset = async (id) => {
    try {
      await api.post(`/admin/users/${id}/reset-password`);
      toast.success("A secure set-password link was emailed to the user.", { duration: 8000 });
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">User management</h1>
          <p className="text-sm text-muted-foreground">{users.length} users · roles: admin, agent, landlord, renter</p>
        </div>
        <button data-testid="user-add-open" onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> Add user</button>
      </div>

      {showForm && (
        <div className="mb-6 rounded-2xl border border-border bg-card p-5 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1" htmlFor="user-form-name">Name</label>
            <input id="user-form-name" data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-xl border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" htmlFor="user-form-email">Email</label>
            <input id="user-form-email" data-testid="user-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-xl border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" htmlFor="user-form-role">Role</label>
            <select id="user-form-role" data-testid="user-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="rounded-xl border border-input bg-background px-3 py-2 text-sm">
              <option value="agent">Agent</option><option value="admin">Admin</option><option value="landlord">Landlord</option>
            </select>
          </div>
          <button data-testid="user-form-create" onClick={create} className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold">Create</button>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border/60 last:border-0">
                <td className="px-5 py-3 font-medium">{u.name}</td>
                <td className="px-5 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-5 py-3">
                  <select data-testid={`user-role-${u.id}`} value={u.role} onChange={(e) => patch(u.id, { role: e.target.value })} aria-label="Role"
                    className="rounded-lg border border-input bg-background px-2 py-1 text-xs">
                    {["admin", "agent", "landlord", "renter"].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-5 py-3">
                  <button data-testid={`user-status-${u.id}`} onClick={() => patch(u.id, { status: u.status === "disabled" ? "active" : "disabled" })}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${u.status === "disabled" ? "bg-coral-100 text-coral-800" : "bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100"}`}>
                    {u.status === "disabled" ? "disabled" : "active"}
                  </button>
                </td>
                <td className="px-5 py-3">
                  <button data-testid={`user-reset-${u.id}`} onClick={() => reset(u.id)} aria-label="Reset password"
                    className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-semibold"><KeyRound className="w-3.5 h-3.5" /> Reset</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
